import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Transient status-toast message with auto-clear. Shared by the RDV and Data Mart workspaces, which
 * each had their own (slightly different) copy. `showStatus(msg)` shows the message and clears it
 * after `durationMs`; repeated calls reset the timer. The timeout is cleared on unmount.
 */
export function useStatusToast(durationMs = 2800): {
  statusMessage: string | null;
  showStatus: (message: string) => void;
  clearStatus: () => void;
} {
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  const clearStatus = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    setStatusMessage(null);
  }, []);

  const showStatus = useCallback(
    (message: string) => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      setStatusMessage(message);
      timeoutRef.current = window.setTimeout(() => {
        setStatusMessage(null);
      }, durationMs);
    },
    [durationMs]
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return { statusMessage, showStatus, clearStatus };
}
